/* =============================================================
   StudentUniversity.jsx — My Exchange University (student view)
   -------------------------------------------------------------
   Routing logic:
     Incoming students  → host university profile from the
                          `universities` table (keyed by
                          student.university_id — the tenant's
                          own Unixplore profile, managed in Settings).
     Outgoing students  → partner university profile from the
                          `partner_universities` table (keyed by
                          student.partner_id — set by the IO admin
                          when creating / editing the student record).

   Both paths produce the same data shape so the render is shared.
   The `type` badge (BILATERAL / ERASMUS / DOUBLE DEGREE) only exists
   on partner_universities rows, so it is omitted for incoming students.

   Layout mirrors the admin read-only view:
     • Left card  (col-span-7): university header, exchange details,
                                 at-a-glance stats, who to contact
     • Top right  (col-span-5): Useful Links
     • Bottom right(col-span-5): Key Dates

   Cache strategy: module-level Map keyed by source + UUID.
   Survives unmount/remount without flash.
   ============================================================= */

import { useState, useEffect }   from 'react';
import StudentLayout              from '../../components/StudentLayout';
import { supabase }              from '../../lib/supabase';
import { useStudentContext }      from '../../hooks/useStudentContext';

/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
// Key format: "uni:<uuid>" for incoming, "partner:<uuid>" for outgoing.
// Entries expire after 5 minutes so stale data (e.g. abbreviation changes) are picked up.
const _cache = new Map();        // key → { data, ts }
const CACHE_TTL = 5 * 60 * 1000; // 5 min

function cacheGet(key) {
  const entry = _cache.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.ts > CACHE_TTL) { _cache.delete(key); return undefined; }
  return entry.data;
}
function cacheSet(key, data) {
  _cache.set(key, { data, ts: Date.now() });
}

/* ─── COLOR MAP (mirrors PartnerProfile.jsx) ─────────────────── */
const COLOR_CLASSES = {
  red:     { bg: 'bg-red-100 text-red-700',         dot: 'bg-red-400'     },
  amber:   { bg: 'bg-amber-100 text-amber-700',     dot: 'bg-amber-400'   },
  blue:    { bg: 'bg-blue-100 text-blue-700',        dot: 'bg-blue-400'    },
  indigo:  { bg: 'bg-indigo-100 text-indigo-700',   dot: 'bg-indigo-400'  },
  slate:   { bg: 'bg-slate-100 text-slate-600',     dot: 'bg-slate-400'   },
  emerald: { bg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  violet:  { bg: 'bg-violet-100 text-violet-700',   dot: 'bg-violet-400'  },
  rose:    { bg: 'bg-rose-100 text-rose-700',       dot: 'bg-rose-400'    },
};

/* ─── SHARED SELECT COLUMNS ──────────────────────────────────── */
const UNI_COLUMNS = `
  id, name, country, abbreviation,
  campus, business_school, language_of_instruction, exchange_period,
  contact_emails, useful_links, key_dates
`.trim();


/* ─── STUDENT UNIVERSITY COMPONENT ─────────────────────────── */
export default function StudentUniversity() {
  const { student, loading: studentLoading } = useStudentContext();

  // Determine routing: incoming → host university, outgoing → partner university
  const isIncoming  = student?.type === 'incoming';
  const isOutgoing  = student?.type === 'outgoing';

  // The lookup key depends on direction
  const fetchId     = isIncoming  ? (student?.university_id ?? null)
                    : isOutgoing  ? (student?.partner_id    ?? null)
                    : null;

  const cacheKey    = fetchId
    ? (isIncoming ? `uni:${fetchId}` : `partner:${fetchId}`)
    : null;

  const _cached     = cacheKey ? cacheGet(cacheKey) : undefined;

  const [uni,     setUni]     = useState(_cached ?? null);
  const [loading, setLoading] = useState(_cached === undefined && !!fetchId);

  useEffect(() => {
    if (!fetchId || !cacheKey) { setLoading(false); return; }
    const hit = cacheGet(cacheKey);
    if (hit) { setUni(hit); setLoading(false); return; }

    setLoading(true);
    (async () => {
      try {
        let data = null;
        let error = null;

        if (isIncoming) {
          // Fetch the host university's profile from the universities table.
          // This is the tenant's own Unixplore profile managed in Settings.
          ({ data, error } = await supabase
            .from('universities')
            .select(UNI_COLUMNS)
            .eq('id', fetchId)
            .maybeSingle());
        } else {
          // Fetch the partner university the IO admin assigned to this student.
          ({ data, error } = await supabase
            .from('partner_universities')
            .select(UNI_COLUMNS)
            .eq('id', fetchId)
            .maybeSingle());
        }

        if (error) console.error('[StudentUniversity] fetch error:', error.message);
        if (data) {
          cacheSet(cacheKey, data);
          setUni(data);
        }
      } catch (err) {
        console.error('[StudentUniversity] unexpected error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchId, cacheKey, isIncoming]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Abbr: always auto-generated from name (same logic as Settings.jsx) ── */
  const abbr = uni?.name
    ? uni.name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 3).toUpperCase()
    : '?';

  /* ── Type badge (partner_universities only) ─────────────── */
  const typeBadge =
    uni?.type === 'ERASMUS'       ? 'bg-blue-100 text-blue-700'    :
    uni?.type === 'DOUBLE DEGREE' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-violet-100 text-violet-700';

  /* ─────────────────────────────────────────────────────────── */
  /*  LOADING STATE                                               */
  /* ─────────────────────────────────────────────────────────── */
  if (studentLoading || loading) return (
    <StudentLayout>
      <div className="mb-8 animate-pulse">
        <div className="h-8 bg-slate-100 rounded w-64 mb-2" />
        <div className="h-4 bg-slate-100 rounded w-48" />
      </div>
      <div className="grid grid-cols-12 gap-6 animate-pulse">
        <div className="col-span-7 space-y-4">
          <div className="h-72 bg-slate-100 rounded-2xl" />
          <div className="h-32 bg-slate-100 rounded-2xl" />
        </div>
        <div className="col-span-5 space-y-4">
          <div className="h-48 bg-slate-100 rounded-2xl" />
          <div className="h-48 bg-slate-100 rounded-2xl" />
        </div>
      </div>
    </StudentLayout>
  );

  /* ─────────────────────────────────────────────────────────── */
  /*  NO UNIVERSITY ASSIGNED / RESOLVED                           */
  /* ─────────────────────────────────────────────────────────── */
  if (!fetchId || !uni) return (
    <StudentLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">My Exchange University</h1>
        <p className="text-[#464555] mt-1">Everything you need to know about your destination.</p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
        <span className="material-symbols-outlined text-slate-200 text-[48px]">account_balance</span>
        <p className="text-slate-500 font-medium mt-3">No exchange university assigned yet.</p>
        <p className="text-slate-400 text-sm mt-1">Your International Office will assign your destination university once your application is approved.</p>
      </div>
    </StudentLayout>
  );

  /* ─────────────────────────────────────────────────────────── */
  /*  MAIN VIEW                                                   */
  /* ─────────────────────────────────────────────────────────── */
  const usefulLinks = uni.useful_links ?? [];
  const keyDates    = uni.key_dates    ?? [];

  return (
    <StudentLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">My Exchange University</h1>
        <p className="text-[#464555] mt-1">Everything you need to know about your destination.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* ── LEFT: University card (spans both rows) ── */}
        <div className="col-span-7 row-span-2 space-y-0">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

            {/* Blue header */}
            <div className="bg-[#4343d5] px-6 py-8 flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold font-display text-xl">{abbr}</span>
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">{uni.name}</h2>
                <p className="text-indigo-200 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px]">location_on</span>
                  {uni.country}
                </p>
              </div>
              {/* Show agreement type badge only for outgoing (partner_universities have this field) */}
              {!isIncoming && uni.type && (
                <span className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full bg-white/20 text-white">
                  {uni.type}
                </span>
              )}
            </div>

            {/* Exchange details */}
            <div className="p-6 grid grid-cols-2 gap-x-8 gap-y-5">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Exchange Period</p>
                <p className="text-sm font-medium text-[#0b1c30] flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-[14px] text-slate-400">calendar_month</span>
                  {uni.exchange_period || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Campus</p>
                <p className="text-sm font-medium text-[#0b1c30]">{uni.campus || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Faculty / Business School</p>
                <p className="text-sm font-medium text-[#0b1c30]">{uni.business_school || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Language of Instruction</p>
                <p className="text-sm font-medium text-[#0b1c30]">{uni.language_of_instruction || '—'}</p>
              </div>
            </div>

            {/* Who to Contact */}
            {(uni.contact_emails ?? []).length > 0 && (
              <div className="border-t border-slate-100">
                <div className="px-6 py-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-[18px] text-indigo-600">contacts</span>
                  <h2 className="font-display text-base font-semibold text-[#0b1c30]">Who to Contact</h2>
                </div>
                <div className="px-6 pb-6 space-y-4">
                  {(uni.contact_emails ?? []).map((c, i) => {
                    const initials = (c.name ?? '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-sm font-semibold flex-shrink-0">
                          {initials}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#0b1c30]">{c.name || 'International Office'}</p>
                          {c.role && <p className="text-xs text-slate-400 mb-1.5">{c.role}</p>}
                          <a
                            href={`mailto:${c.email}`}
                            className="text-sm text-indigo-600 font-medium hover:underline flex items-center gap-1"
                          >
                            <span className="material-symbols-outlined text-[14px]">mail</span>
                            {c.email}
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── TOP RIGHT: Useful Links ── */}
        <div className="col-span-5">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">link</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Useful Links</h2>
            </div>
            <div className="p-4">
              {usefulLinks.length === 0 ? (
                <p className="text-sm text-slate-400 px-3 py-3">No links added yet.</p>
              ) : (
                <div className="space-y-1">
                  {usefulLinks.map((link, i) => (
                    <a
                      key={i}
                      href={link.url || '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group"
                    >
                      <span className="material-symbols-outlined text-[18px] text-slate-400 group-hover:text-indigo-500 transition-colors">link</span>
                      <span className="text-sm text-[#464555] group-hover:text-indigo-600 transition-colors">{link.label || link.url}</span>
                      <span className="material-symbols-outlined text-[14px] text-slate-300 ml-auto group-hover:text-indigo-400 transition-colors">open_in_new</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── BOTTOM RIGHT: Key Dates ── */}
        <div className="col-span-5">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">event</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Key Dates</h2>
            </div>
            <div className="p-4">
              {keyDates.length === 0 ? (
                <p className="text-sm text-slate-400 px-3 py-3">No key dates added yet.</p>
              ) : (
                <div className="space-y-1">
                  {keyDates.map((d, i) => {
                    const c = COLOR_CLASSES[d.color] ?? COLOR_CLASSES.indigo;
                    return (
                      <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                        <div className={`flex-shrink-0 whitespace-nowrap text-center text-xs font-bold px-2 py-1 rounded-lg ${c.bg}`}>
                          {d.date}
                        </div>
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
                        <span className="text-sm text-[#464555]">{d.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </StudentLayout>
  );
}
